import React, { useEffect, useState } from 'react'
import { useGatherAsk } from '../../../../marketing-v2/features/social/data/askData'
import { postTheme } from '../../../../marketing-v2/features/social/render/render'
import { POST_TYPES } from '../../../../marketing-v2/features/social/data/postTypes'
import { savePost, loadPosts } from '../../../../marketing-v2/features/social/data/store'
import { townOf } from '../../../../marketing-v2/features/social/data/captions'
import Preview from '../../../../marketing-v2/features/social/components/Preview'
import '../../../../marketing-v2/features/social/Social.css'
import { hapticTick, hapticSuccess } from '../../../../../_utils/haptics'
import { buildPostFrom } from '../makeThings'
import { useBrand, useRecall } from '../useBeu'
import { Tick, Photo } from './icons'
import { SOCIAL_PATH, plannedSaid, editByAsking } from './PostWidget'
import { useSaver, useEditor } from '../saveBus'

// A few posts at once (a week of posts): drawn one after another while the
// salon reads on, then saved to Social together when they say so
const PostsWidget = ({ block, id, compact, go, latest = false }) => {
  const brand = useBrand()
  const gather = useGatherAsk()
  const [memo, setMemo] = useRecall(id)
  // One place per item, in order (null where one couldn't be made)
  const savedIds = (memo && memo.postIds) || null
  const all = savedIds ? loadPosts() : []
  const savedPosts = savedIds ? savedIds.map(pid => (pid && all.find(p => p.id === pid)) || null) : null
  const [made, setMade] = useState([])
  const [, bump] = useState(0)
  const total = block.items.length

  useEffect(() => {
    if (savedPosts || !brand.ready) return undefined
    let alive = true
    const opts = { gather, kit: brand.kit, trade: brand.market, salonName: brand.salonName, town: townOf(brand.salon && brand.salon.formattedAddress) }
    ;(async () => {
      for (let i = 0; i < block.items.length; i += 1) {
        let r = null
        try { r = await buildPostFrom(block.items[i], opts) } catch (e) { r = null } // eslint-disable-line no-await-in-loop
        if (!alive) return
        setMade(m => [...m, r])
      }
    })()
    return () => { alive = false }
  }, [id, brand.ready]) // eslint-disable-line react-hooks/exhaustive-deps

  // Changed by asking in the chat (before saving), by position
  const edited = (memo && memo.edited) || []
  const posts = savedPosts || made.map((r, i) => (edited[i] || (r ? r.post : null)))
  const ready = posts.filter(Boolean)
  const drawing = !savedPosts && made.length < total

  const saveAll = () => {
    if (savedPosts) return savedPosts
    const ids = posts.map(p => (p ? savePost({ ...p, status: p.plannedAt ? 'planned' : 'draft' }).id : null))
    setMemo({ postIds: ids })
    hapticSuccess()
    return ids
  }
  const openOne = (i) => {
    hapticTick()
    const ids = savedIds || saveAll()
    if (ids[i]) go(SOCIAL_PATH, { openPostId: ids[i] })
  }
  const planned = ready.filter(p => p.plannedAt).length
  // "Less formal", "make them pink" in the chat: every post here changes in
  // place, one after another (saved ones stay saved, now changed)
  useEditor(id, latest && !drawing && ready.length > 0, async (text) => {
    const next = posts.slice()
    let changed = 0
    const said = []
    for (let i = 0; i < posts.length; i += 1) {
      if (!posts[i]) continue // eslint-disable-line no-continue
      const res = await editByAsking(posts[i], text, brand) // eslint-disable-line no-await-in-loop
      if (!res) continue // eslint-disable-line no-continue
      changed += 1
      next[i] = res.post
      res.changes.slice(0, 2).forEach(c => { if (!said.includes(c.toLowerCase())) said.push(c.toLowerCase()) })
    }
    if (!changed) return null
    if (savedPosts) { next.forEach((p, i) => { if (p && savedIds[i]) savePost({ ...p, id: savedIds[i], status: p.plannedAt ? 'planned' : (p.status === 'posted' ? 'posted' : 'draft') }) }); bump(n => n + 1) } else setMemo({ ...(memo || {}), edited: next })
    return `${changed} of the ${posts.filter(Boolean).length} posts: ${said.slice(0, 4).join(', ')}`
  })
  // "Yes" in the chat saves them, exactly as Plan all / Save all does
  useSaver(id, latest && !savedPosts && !drawing && ready.length > 0, () => {
    saveAll()
    const when = ready.filter(p => p.plannedAt).map(p => plannedSaid(p.plannedAt))
    const what = `${ready.length} ${ready.every(p => p.format === 'story') ? (ready.length === 1 ? 'Story' : 'Stories') : (ready.length === 1 ? 'post' : 'posts')}`
    return when.length ? `${what} planned in Social (${when.join(', ')})` : `${what} saved as drafts in Social`
  })

  return (
    <div className={`bw-card is-wide${savedPosts ? ' is-done' : ''}`} role="group" aria-label={block.title || 'Posts'}>
      <div className="bw-head">
        <span className="bw-kind"><Photo />{block.title || `${total} posts`}</span>
        {savedPosts && <span className="bw-status is-done"><Tick size={12} />Saved in Social</span>}
        {drawing && <span className="bw-status" role="status">Drawing {Math.min(made.length + 1, total)} of {total}</span>}
      </div>
      <div className="bw-strip" role="list">
        {block.items.map((item, i) => {
          const post = posts[i]
          return (
            <div key={`${i}-${item.ask || item.kind}`} className="bw-strip-item" role="listitem">
              {post ? (
                <button type="button" className={`bw-art${post.format === 'story' ? ' is-story' : ''}`} onClick={() => openOne(i)} aria-label={`Open ${(POST_TYPES[post.type] || {}).label || 'this post'} in the studio`}>
                  <Preview post={post} theme={postTheme(post, { kit: brand.kit })} format={post.format} />
                </button>
              ) : <span className="bw-art bw-shimmer" aria-hidden />}
              <span className="bw-strip-name">{post ? ((POST_TYPES[post.type] || {}).label || 'Post') : 'Drawing'}</span>
              {post && post.plannedAt && !compact && <span className="bw-strip-when">{plannedSaid(post.plannedAt)}</span>}
            </div>
          )
        })}
      </div>
      <div className="bw-actions">
        <button type="button" className="bw-btn is-secondary" onClick={() => { hapticTick(); go(SOCIAL_PATH) }}>Open Social</button>
        {!savedPosts && (
          <button type="button" className="bw-btn is-primary" disabled={drawing || !ready.length} onClick={saveAll}>
            {planned ? `Plan all ${ready.length || total}` : `Save all ${ready.length || total}`}
          </button>
        )}
      </div>
    </div>
  )
}

export default PostsWidget
